/**
 * Canonical enum/value types for the promotions domain.
 *
 * Naming map vs. legacy identifiers:
 * - `BoostCategory` was the backend `BoostType` enum (`payout_boost` | `risk_free`).
 * - `PromotionTabType` was `PromotionType` (BOOSTS | VOUCHERS | DEPOSIT_BONUSES).
 * - `BoostType` is the display alias (`payout` | `no-sweat`) — replaces the dead `BoostTypeDisplay` enum.
 * - Contest `PromotionType` (DNP/daily promo union) was renamed to `ContestPromoType`
 *   and lives in `~/domains/contest/domains/services/interfaces/propsService/api/promotions.ts`.
 */

/**
 * Filter and sort parameters accepted by the universal `usePromoCarousel`
 * data hook. Server-side honoured via `marketing-service/api/promotions`.
 */
// eslint-disable-next-line no-restricted-syntax
export enum PromoCarouselLocation {
  HOME = 'home',
  QUICKPICKS = 'quickpicks',
  CONTEST = 'contest',
  PROMO_CENTER = 'promotions',
}


// eslint-disable-next-line no-restricted-syntax
export enum VoucherType {
  QUICKPICKS = 'QUICKPICKS',
  CONTEST_ID_RESTRICTED = 'CONTEST_ID_RESTRICTED',
  CONTESTS = 'CONTESTS',
  LEAGUES = 'LEAGUES',
  GENERAL = 'GENERAL',
}

// eslint-disable-next-line no-restricted-syntax
export enum VoucherStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
}

// eslint-disable-next-line no-restricted-syntax
export enum PromotionSegmentType {
  PROMOTIONS = 'promotions',
  CHALLENGES = 'challenges',
}

// eslint-disable-next-line no-restricted-syntax
export enum PromotionTabType {
  ALL = 'all',
  BOOSTS = 'boosts',
  VOUCHERS = 'vouchers',
  DEPOSIT_BONUSES = 'deposit_bonuses',
}

/**
 * Granular promotion kind used by cards, modals, analytics.
 */
// eslint-disable-next-line no-restricted-syntax
export enum PromoType {
  CREDIT = 'credit',
  VOUCHER = 'voucher',
  BOOST = 'boost',
  DEPOSIT_BONUS = 'bonus',
  REFERRAL = 'referral',
}

/**
 * Backend boost category discriminator (was the `BoostType` enum).
 */
// eslint-disable-next-line no-restricted-syntax
export enum BoostCategory {
  PAYOUT_BOOST = 'payout_boost',
  RISK_FREE = 'risk_free',
}

/**
 * Display-side boost variant — value namespace + matching string union.
 * Replaces the dead `BoostTypeDisplay` enum and absorbs the design-system
 * `BoostType` type so consumers import it from one place.
 */
export const BoostType = {
  PAYOUT: 'payout',
  NO_SWEAT: 'no-sweat',
} as const;

export type BoostTypeType = (typeof BoostType)[keyof typeof BoostType];
